'use strict';

import { useState, useEffect, useCallback } from 'react';
import BoardStore from '@stores/BoardStore';

/**
 * Hook to check if a plan has dietitian recommendations
 * 
 * @param {string} uuid - Plan UUID to check
 * @returns {Object} Dietitian recommendation state and board content
 */
export const useDietitianRecommendation = (uuid) => {
    const [boardContent, setBoardContent] = useState(null);
    const [isDietitianRecommended, setIsDietitianRecommended] = useState(false);

    /**
     * Get board content for a plan UUID - looks specifically for dietitian recommendations
     */
    const getBoardContentByUuid = useCallback((planUuid) => {
        if (!planUuid) return null;
        
        const boards = BoardStore.getBoardsByResourceId(planUuid);
        const contents = boards.reduce((carry, board) => {
            const bcs = board.contents.filter(
                ({ dietitian_recommendation, resource_id }) => 
                    dietitian_recommendation && resource_id === planUuid
            );
            return carry.concat(bcs);
        }, []);
        
        return contents[0] || null;
    }, []);

    /**
     * Update dietitian recommendation state when boards change
     */
    const updateRecommendationState = useCallback(() => {
        if (!uuid) {
            setBoardContent(null);
            setIsDietitianRecommended(false);
            return;
        }

        const content = getBoardContentByUuid(uuid);
        setBoardContent(content);
        setIsDietitianRecommended(!!content?.dietitian_recommendation);
    }, [uuid, getBoardContentByUuid]);

    // Listen for board changes
    useEffect(() => {
        BoardStore.addChangeListener(updateRecommendationState);
        updateRecommendationState(); // Initial check
        
        return () => BoardStore.removeChangeListener(updateRecommendationState);
    }, [updateRecommendationState]);

    return {
        boardContent,
        isDietitianRecommended,
        getBoardContentByUuid
    };
};